import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, PhoneCall, Sun, HelpCircle, ChevronRight, MessageSquare } from 'lucide-react';

const FAQ = () => {
  const faqs = [
    { 
      id: '01',
      short: 'Savings & ROI',
      question: 'How much can I save with solar panels?', 
      answer: 'Most homeowners reduce their daytime grid dependency instantly, saving between 50% to 85% on their monthly electricity bills. Your exact savings depend on total roof capacity, daily load cycles, and local DISCOM net-metering slabs. The ROI is typically achieved in 3 to 4 years.' 
    },
    { 
      id: '02',
      short: 'System Lifespan',
      question: 'What is the lifespan of solar panels?', 
      answer: 'Premium Tier-1 monocrystalline solar panels are engineered to last 25 to 30 years. Most of our high-efficiency modules come with a certified 25-year linear power output performance warranty, ensuring long-term reliability.' 
    },
    { 
      id: '03',
      short: 'Maintenance',
      question: 'Do I need to maintain my solar system?', 
      answer: 'Solar grids require virtually zero moving parts, making them extremely low-maintenance. A simple water cleaning of the glass surface every 2-3 weeks to clear dust, and an annual structural check-up by our engineers is all it takes.' 
    },
    { 
      id: '04',
      short: 'Installation Time',
      question: 'How long does the physical installation take?', 
      answer: 'For standard residential rooftop setups (3kW to 10kW), physical structural mounting and panel layout alignment takes only 1 to 2 days. We ensure a rapid, hassle-free installation without disturbing your daily routine.' 
    },
    { 
      id: '05',
      short: 'Roof Suitability',
      question: 'Is my roof structure suitable for solar?', 
      answer: 'Most concrete, metal sheet, or tiled roofs with direct sun exposure are ideal. We conduct an advanced 3D structural load assessment of your roof during our preliminary physical mapping to ensure absolute safety and optimal sun angle.' 
    }
  ];

  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <section id="faqs" className="py-12 sm:py-16 bg-[#F8FAFC] relative overflow-hidden font-sans border-y border-slate-200/50">
      
      {/* 🌟 Ambient Glows 🌟 */}
      <div className="absolute top-0 right-0 w-[250px] sm:w-[400px] h-[250px] sm:h-[400px] bg-[#0D3F7F]/5 rounded-full blur-[80px] sm:blur-[100px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[250px] sm:w-[400px] h-[250px] sm:h-[400px] bg-[#F7941D]/5 rounded-full blur-[80px] sm:blur-[100px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* 🌟 1. SECTION HEADER 🌟 */}
        <div className="text-center max-w-2xl mx-auto mb-8 sm:mb-12">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 bg-[#F0F7FF] border border-[#1E5CB3]/20 px-3.5 py-1 sm:px-4 sm:py-1.5 rounded-full mb-3 shadow-sm"
          >
            <HelpCircle className="w-3.5 h-3.5 text-[#F7941D]" />
            <span className="text-[9px] sm:text-[10px] font-bold tracking-[0.15em] text-[#0D3F7F] uppercase">
              Support Desk Hub
            </span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-2xl sm:text-3xl md:text-4xl font-black text-[#0F172A] tracking-tight leading-tight"
          >
            Frequently Asked <span className="bg-gradient-to-r from-[#F7941D] to-[#FFC107] bg-clip-text text-transparent">Questions</span>
          </motion.h2>
        </div>

        {/* 🌟 2. INTERACTIVE DASHBOARD CONTAINER 🌟 */}
        <div className="bg-white rounded-[1.25rem] sm:rounded-[2rem] border border-[#1E5CB3]/10 shadow-[0_10px_40px_rgba(13,63,127,0.05)] overflow-hidden flex flex-col lg:flex-row min-h-0 lg:min-h-[420px]">
          
          {/* LEFT PANEL: Questions List (Pills style with scroll on mobile, full stack on desktop) */}
          <div className="lg:w-2/5 bg-slate-50 border-b lg:border-b-0 lg:border-r border-[#1E5CB3]/5 p-4 sm:p-6 lg:p-8">
            <h3 className="hidden lg:block text-[13px] sm:text-[14px] font-black text-[#0D3F7F] uppercase tracking-widest mb-6">
              Select a Topic
            </h3>

            {/* Questions list container */}
            <div className="flex lg:flex-col gap-3 overflow-x-auto lg:overflow-visible pb-3 lg:pb-0 hide-scrollbar snap-x snap-mandatory px-1 lg:px-0">
              {faqs.map((faq, index) => {
                const isActive = activeIndex === index;
                return (
                  <button
                    key={index}
                    onClick={() => setActiveIndex(index)}
                    className={`relative text-left w-[260px] xs:w-[280px] lg:w-full shrink-0 snap-center sm:snap-start flex items-center justify-between gap-3 p-3.5 sm:p-4 rounded-xl sm:rounded-2xl transition-all duration-300 touch-manipulation ${
                      isActive 
                        ? 'bg-[#0D3F7F] text-white shadow-md shadow-[#0D3F7F]/20 scale-[0.98] lg:scale-100' 
                        : 'bg-white border border-slate-200 text-slate-600 hover:border-[#F7941D]/40 hover:text-[#0F172A]'
                    }`}
                  >
                    <div className="flex flex-col gap-0.5 min-w-0">
                      <span className={`text-[9px] sm:text-[10px] font-bold uppercase tracking-wider ${isActive ? 'text-[#F7941D]' : 'text-slate-400'}`}>
                        0{index + 1} • {faq.short}
                      </span>
                      <span className={`text-xs sm:text-[13px] lg:text-[14px] font-bold truncate lg:normal-case ${isActive ? 'text-white' : 'text-[#0F172A]'}`}>
                        {faq.question}
                      </span>
                    </div>
                    {/* Desktop Arrow Indicator */}
                    <ChevronRight className={`hidden lg:block w-4 h-4 shrink-0 transition-transform ${isActive ? 'text-[#FFC107] translate-x-1' : 'text-slate-300'}`} />
                  </button>
                );
              })}
            </div>
          </div>

          {/* RIGHT PANEL: Dynamic Answer Display */}
          <div className="lg:w-3/5 p-5 sm:p-8 lg:p-12 flex flex-col justify-between bg-white relative">
            
            <div className="my-auto">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeIndex}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.25, ease: "easeOut" }}
                  className="relative z-10"
                >
                  {/* Decorative Icon */}
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#F0F7FF] flex items-center justify-center text-[#F7941D] mb-4 sm:mb-6">
                    <MessageSquare className="w-4 h-4 sm:w-5 sm:h-5" />
                  </div>

                  <h3 className="text-lg sm:text-xl lg:text-2xl font-black text-[#0F172A] leading-snug sm:leading-tight mb-3 sm:mb-4">
                    {faqs[activeIndex].question}
                  </h3>
                  
                  <p className="text-xs sm:text-sm lg:text-[15px] text-slate-500 font-medium leading-relaxed">
                    {faqs[activeIndex].answer}
                  </p>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Compact Call to Action integrated inside the Answer Panel */}
            <div className="mt-8 sm:mt-10 pt-5 sm:pt-6 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-4 w-full">
              <div className="flex items-center gap-3 w-full sm:w-auto">
                <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-[#0D3F7F]/10 flex items-center justify-center text-[#0D3F7F] shrink-0">
                  <PhoneCall className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                </div>
                <div className="text-left">
                  <h4 className="text-xs sm:text-[12px] font-bold text-[#0D3F7F] leading-none mb-1">Still have doubts?</h4>
                  <p className="text-[10px] sm:text-[11px] text-slate-500 font-medium">Talk to our experts directly.</p>
                </div>
              </div>

              <a 
                href="#contact" 
                className="w-full sm:w-auto bg-[#F7941D] hover:bg-[#E67A00] text-white text-xs sm:text-[12px] font-bold py-3 px-6 rounded-full flex items-center justify-center gap-2 transition-all shadow-[0_4px_15px_rgba(247,148,29,0.25)] hover:shadow-[0_6px_20px_rgba(247,148,29,0.35)] active:scale-95 shrink-0 touch-manipulation"
              >
                <span>Request Call</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </a>
            </div>

          </div>
        </div>

      </div>

      {/* Helper CSS for hiding scrollbar on mobile horizontal scroll */}
      <style dangerouslySetInnerHTML={{__html: `
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}} />
    </section>
  );
};

export default FAQ;